import React, { useState, useMemo, useEffect, useCallback } from 'react';
import { useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAsync } from 'react-async';
import { EntityHistoryList } from '@koob/entity-history';
import { getEntityHistory } from 'api/node/entityHistory';
import { getToConnectionRequestById } from 'api/node/toConnectionRequest';
import ConnectionRequestPageHeader from '../Edit/Partials/ConnectionRequestPageHeader';
import { useApp } from 'hooks';
import { decodeId } from 'utils';
import ToConnectionRequestHistoryCard from './ToConnectionRequestHistoryCard';
import { KoobCard, KoobPagination, KoobPlaceholder } from '@koob/ui';
import { PER_PAGE_FOR_LIST } from 'constants';

const ToConnectionRequestHistory = () => {
  const { t } = useTranslation('toConnectionRequest');
  const { requestId } = useParams();
  const { currentOrganization, selectedDistributionOrganization } = useApp();

  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(PER_PAGE_FOR_LIST);
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const dmcId =
    selectedDistributionOrganization?.value ?? currentOrganization?.id;

  const { data: requestData, error: requestError } = useAsync({
    promiseFn: getToConnectionRequestById,
    requestId: requestId,
    dmcId: decodeId(dmcId),
  });

  const fetchHistory = useCallback(async () => {
    setIsLoading(true);
    try {
      const response = await getEntityHistory({
        entityType: 'ToConnectionRequest',
        entityId: requestId,
        page,
        perPage,
      });
      setData(response);
    } catch (error) {
      console.error('Failed to fetch connection request history:', error);
    } finally {
      setIsLoading(false);
    }
  }, [requestId, page, perPage]);

  useEffect(() => {
    fetchHistory();
  }, [fetchHistory]);

  const items = data?.data?.items ?? [];

  const totalPages = useMemo(() => {
    const total = data?.data?.total;
    const limit = data?.data?.limit;
    if (total !== undefined && limit) {
      return Math.ceil(total / limit);
    }
    return 1;
  }, [data]);

  // Field labels for ToConnectionRequest
  const fieldLabels = useMemo(
    () => ({
      state: t('field.state', 'State'),
      margin: t('field.margin', 'Margin'),
      isMarginPercent: t('field.isMarginPercent', 'Margin Type'),
      explaination: t('field.explaination', 'Explanation'),
      currency: t('field.currency', 'Currency'),
      partnerCancelCondition: t(
        'field.partnerCancelCondition',
        'Partner Cancel Condition',
      ),
      showToTa: t('field.showToTa', 'Show to TA'),
      requestHotels: t('field.requestHotels', 'Hotels'),
      requestExperiences: t('field.requestExperiences', 'Experiences'),
      requestTemplates: t('field.requestTemplates', 'Templates'),
    }),
    [t],
  );

  const renderCard = useCallback(
    (history, index) => (
      <ToConnectionRequestHistoryCard
        key={history.id || index}
        history={history}
        fieldLabels={fieldLabels}
      />
    ),
    [fieldLabels],
  );

  return (
    <>
      <ConnectionRequestPageHeader
        request={requestData?.data}
        loadError={Boolean(requestError)}
      />
      <div className="py-4 px-4">
        {isLoading ? (
          <KoobCard>
            <div className="space-y-4">
              <KoobPlaceholder className="h-24 w-full rounded-lg" />
              <KoobPlaceholder className="h-24 w-full rounded-lg" />
              <KoobPlaceholder className="h-24 w-full rounded-lg" />
            </div>
          </KoobCard>
        ) : (
          <KoobCard padding="none">
            <div className="p-6">
              <div className="min-h-[400px]">
                <EntityHistoryList
                  items={items}
                  isLoading={false}
                  emptyMessage={t('history.empty')}
                  renderCard={renderCard}
                />
              </div>
            </div>
            {items.length > 0 && (
              <KoobPagination
                embedded
                page={page}
                setPage={setPage}
                totalPages={totalPages}
                totalCount={data?.data?.total}
                perPage={perPage}
                setPerPage={(newPerPage) => {
                  setPerPage(newPerPage);
                  setPage(1);
                }}
              />
            )}
          </KoobCard>
        )}
      </div>
    </>
  );
};

export default ToConnectionRequestHistory;
